import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { tap, catchError } from 'rxjs';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { BasicUser } from 'src/app/core-module/models/basic-user';
import { AuditLocation } from 'src/app/core-module/models/Locations/audit-location';
import { UserLocationLocationSave } from 'src/app/core-module/models/UserManagement/user-location-location-save';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';

@Component({
    selector: 'app-user-locations',
    templateUrl: './user-locations.component.html',
    styleUrl: './user-locations.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserLocationsComponent {

  public isGroup: boolean = false;
  private _userList: BasicUser[] = [];
  public userListView: BasicUser[] = [];
  public userFilterString: string = "";
  public selectedUser: BasicUser = new BasicUser();
  public isLoadingLocations: boolean = false;
  private _locationList: AuditLocation[] = [];
  public locationListView: AuditLocation[] = [];
  public minLocationLevel: number = -1;

  public allPermissions: boolean = false;
  public showUsersDDL: boolean = true;
  public confirmChange: boolean = false;
  public showActionSectionBar: boolean = false;

  constructor(private router: Router, private UMS: UserManagementService, private toaster: ToastrService) {
    if (this.router.url == '/settings/users/grouplocations') {
      this.isGroup = true;
    }
    this.getUsers();
  }

  getUsers() {
    this.UMS.getUserLocationUsers(this.isGroup).pipe(
      tap((a:BasicUser[]) => {
        this.userFilterString = "";
        this.buildUsers(a)
      }),
    catchError((err:any) => {
      console.error('ulc001')
      this.toaster.addToastr(new Toastr({ Type: 3, Message: "An error occurred collecting the information, please try again later." }));
      return err;
    })
    ).subscribe();
  }
  buildUsers(returnedUsers: BasicUser[]) {
    this._userList = [];
    returnedUsers.forEach(e => {
      this._userList.push(new BasicUser(e));
    });
    var pleaseSelect = new BasicUser({ UserID: -1, DisplayName: "Please Select..." });
    this._userList.unshift(pleaseSelect);
    this.selectedUser = pleaseSelect;
    this.buildUserView();
  }
  buildUserView() {
    if (this.userFilterString.length == 0) {
      this.userListView = this._userList;
      return;
    }
    this.userListView = this._userList.filter(e => { return e.DisplayName.toLowerCase().includes(this.userFilterString.toLowerCase()) })
  }
  loadUser() {
    this.isLoadingLocations = true;
    if (this.selectedUser.UserID == -1) {
      this.isLoadingLocations = false;
      return;
    }

    this.UMS.getUserLocationLocations(this.selectedUser.UserID).pipe(
      tap((a:AuditLocation[]) => {
        this.buildLocations(a)
        this.isLoadingLocations = false;
        this.showActionSectionBar = true;
      }),
    catchError((err:any) => {
      console.error('ulc002')
      this.toaster.addToastr(new Toastr({ Type: 3, Message: "An error occurred collecting the information, please try again later." }));
      return err;
    })
    ).subscribe();
  }
  buildLocations(locations: AuditLocation[]) {
    this._locationList = [];
    locations.forEach(e => {
      this._locationList.push(new AuditLocation(e));
    });
    this.findLowestLevel();
    this.arrangeLocations();
    this.buildLocationView();
  }
  resetLocations() {
    this.buildLocationView();
  }
  buildLocationView() {
    this.locationListView = [];
    this._locationList.forEach(e => {
      this.locationListView.push(new AuditLocation(e));
    })
    this.showUsersDDL = false;
  }
  findLowestLevel() {
    this.minLocationLevel = -1;
    this._locationList.forEach(e => {
      if (this.minLocationLevel == -1) {
        this.minLocationLevel = e.LocationLevelSeq;
      } else if (this.minLocationLevel > e.LocationLevelSeq) {
        this.minLocationLevel = e.LocationLevelSeq;
      }
    });
  }
  arrangeLocations() {
    const locationsById = new Map<number, AuditLocation>();
    const locationsByParent = new Map<number, AuditLocation[]>();
    const sortedLocations: AuditLocation[] = [];
    const nameSort = (a: AuditLocation, b: AuditLocation) => {
      const nameCompare = a.LocationName.localeCompare(b.LocationName);
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.LocationID - b.LocationID;
    };

    this._locationList.forEach(location => {
      locationsById.set(location.LocationID, location);
      const parentKey = location.ParentLocationID ?? -1;
      if (!locationsByParent.has(parentKey)) {
        locationsByParent.set(parentKey, []);
      }
      locationsByParent.get(parentKey)!.push(location);
    });

    locationsByParent.forEach(children => {
      children.sort(nameSort);
    });

    const roots = (locationsByParent.get(-1) ?? []).slice();
    this._locationList.forEach(location => {
      if (location.ParentLocationID != -1 && !locationsById.has(location.ParentLocationID)) {
        roots.push(location);
      }
    });
    roots.sort(nameSort);

    const addWithChildren = (location: AuditLocation) => {
      sortedLocations.push(location);
      const children = locationsByParent.get(location.LocationID) ?? [];
      children.forEach(child => addWithChildren(child));
    };

    roots.forEach(location => addWithChildren(location));
    this._locationList = sortedLocations;
  }
  updateAllPermissions() {

    this.locationListView.forEach(e => {
      e.HasPermission = this.allPermissions;
    })
  }
  validateAllPermissions() {
    var hasAllPermissions = true;
    this.locationListView.forEach(e => {
      if (!e.HasPermission) {
        hasAllPermissions = false;
      }
    })
    this.allPermissions = hasAllPermissions;
  }
  clearLocationList() {
    this.locationListView = [];
    this._locationList = [];
  }
  validateUserChange() {
    if (this.checkForChange()) {
      this.confirmChange = true;
    } else {
      this.showUsersDDL = true;
    }
  }
  checkForChange(): boolean {
    var hasChanged: boolean = false;
    this._locationList.forEach(e => {
      if (this.locationListView.find(f => { return f.LocationID == e.LocationID })?.HasPermission != e.HasPermission) {
        hasChanged = true;
      }
    })
    return hasChanged;
  }
  showDDL() {
    this.confirmChange = false;
    this.showUsersDDL = true;
    this.clearLocationList();
    this.showActionSectionBar = false;
  }
  validateSave() {

    this.UMS.saveUserLocationLocations({ UserID: this.selectedUser.UserID, IsGroup: this.isGroup, Locations: this.locationListView }).pipe(
      tap((a:BasicResult) => {
        if (a.IsError) {
          this.toaster.addToastr(new Toastr({ Type: 3, Message: "Unable to update user locations, please try again." }));
          console.error(a.ErrorCode);
        } else {
          this.toaster.addToastr(new Toastr({ Type: 2, Message: "User locations updated." }));
        }

        //reload location list
        this.loadUser();
      }),
          catchError((err:any) => {
            console.error('ulc003');
            this.toaster.addToastr(new Toastr({ Type: 3, Message: "An error occurred while saving, please try again later." }));
            return err;
          })).subscribe();
  }
  goBack() {
    this.router.navigateByUrl("/settings");
  }
}
